import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  NotificationMessage,
  WebSocketSubscription,
} from "@/services/websocket.service";
import useApi from "@/services/use.api";

const { useDeleteApi, useGetApi } = useApi();

export interface NotificationStore {
  loading: boolean;
  notifications: NotificationMessage[];
  subscriptions: WebSocketSubscription[];
  unreadCount: number;

  // Actions
  addNotification: (notification: NotificationMessage) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  cleanOldNotifications: (daysToKeep?: number) => void;

  // fetchNotifications: () => Promise<void>;
  setLoading: (loading: boolean) => void;

  // Subscriptions
  updateSubscriptions: (subscriptions: WebSocketSubscription[]) => void;
  toggleSubscription: (topic: string) => void;
  getSubscription: (topic: string) => WebSocketSubscription | undefined;

  // Getters
  getUnreadNotifications: () => NotificationMessage[];
  getNotificationsByType: (type: string) => NotificationMessage[];
  getNotificationCount: () => number;
  getOldestNotification: () => NotificationMessage | undefined;
  getLatestNotification: () => NotificationMessage | undefined;
}

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      loading: false,
      notifications: [],
      subscriptions: [
        { topic: "/campagne", enabled: true },
        { topic: "/commande", enabled: true },
        { topic: "/utilisateur", enabled: false },
      ],
      unreadCount: 0,
      setLoading: (loading: boolean) => set({ loading }),

      addNotification: (notification: NotificationMessage) => {
        set((state) => {
          // Éviter les doublons basés sur l'ID
          const exists = state.notifications.some(
            (n) => n.id === notification.id
          );
          if (exists) {
            return state;
          }

          return {
            notifications: [notification, ...state.notifications],
            unreadCount: state.unreadCount + 1,
          };
        });
      },

      markAsRead: (notificationId: string) => {
        set((state) => ({
          notifications: state.notifications.map((notif) =>
            notif.id === notificationId ? { ...notif, read: true } : notif
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((notif) => ({
            ...notif,
            read: true,
          })),
          unreadCount: 0,
        }));
      },

      removeNotification: async (notificationId: string) => {
        set((state) => {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          const wasUnread = notification && !notification.read;

          return {
            notifications: state.notifications.filter(
              (notif) => notif.id !== notificationId
            ),
            unreadCount: wasUnread
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
          };
        });
      },

      clearAllNotifications: () => {
        get().notifications.forEach((notif) => {
          get().removeNotification(notif.id);
        });

        set({
          notifications: [],
          unreadCount: 0,
        });
      },

      updateSubscriptions: (subscriptions: WebSocketSubscription[]) => {
        set({ subscriptions });
      },

      toggleSubscription: (topic: string) => {
        set((state) => ({
          subscriptions: state.subscriptions.map((sub) =>
            sub.topic === topic ? { ...sub, enabled: !sub.enabled } : sub
          ),
        }));
      },

      getSubscription: (topic: string) => {
        return get().subscriptions.find((sub) => sub.topic === topic);
      },

      cleanOldNotifications: (daysToKeep: number = 30) => {
        set((state) => {
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

          const filteredNotifications = state.notifications.filter(
            (notification) => new Date(notification.timestamp) > cutoffDate
          );

          const removedUnreadCount = state.notifications.filter(
            (notification) =>
              new Date(notification.timestamp) <= cutoffDate &&
              !notification.read
          ).length;

          return {
            notifications: filteredNotifications,
            unreadCount: Math.max(0, state.unreadCount - removedUnreadCount),
          };
        });
      },

      getUnreadNotifications: () => {
        return get().notifications.filter((n) => !n.read);
      },

      getNotificationsByType: (type: string) => {
        return get().notifications.filter((n) => n.type === type);
      },

      getNotificationCount: () => {
        return get().notifications.length;
      },

      getOldestNotification: () => {
        const notifications = get().notifications;
        if (notifications.length === 0) return undefined;
        return notifications.reduce((oldest, current) =>
          new Date(current.timestamp) < new Date(oldest.timestamp)
            ? current
            : oldest
        );
      },

      getLatestNotification: () => {
        const notifications = get().notifications;
        if (notifications.length === 0) return undefined;
        return notifications.reduce((latest, current) =>
          new Date(current.timestamp) > new Date(latest.timestamp)
            ? current
            : latest
        );
      },
    }),
    {
      name: "notification-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
        subscriptions: state.subscriptions,
        unreadCount: state.unreadCount,
      }),
    }
  )
);
